import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { verifyPaymentStatus } from '../../redux/paymentSlice';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // verifying, success, failed
  const [paymentDetails, setPaymentDetails] = useState(null);
  
  const { verifyLoading, verifyError } = useSelector(state => state.payment);
  
  useEffect(() => {
    // Başarılı ödemede pending slot bilgisini temizle
    sessionStorage.removeItem("pendingAvailabilityId");
    sessionStorage.removeItem("pendingTherapistId");
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (sessionId) {
      // Session ID'den payment ID'yi çıkarmaya çalış veya backend'den payment'ı ara
      // Eğer payment ID'niz var ise direkt kullanın
      const paymentId = searchParams.get('payment_id');
      
      if (paymentId) {
        dispatch(verifyPaymentStatus(paymentId))
          .unwrap()
          .then((result) => {
            setPaymentDetails(result.data);
            if (result.data.status === 'COMPLETED') {
              setVerificationStatus('success');
              // Ödeme başarılı olduğunda userTherapyPlanner sayfasına yönlendir
              setTimeout(() => {
                navigate('/userTherapyPlanner');
              }, 3000); // 3 saniye bekleyip yönlendir
            } else {
              setVerificationStatus('failed');
            }
          })
          .catch((error) => {
            console.error('Payment verification failed:', error);
            setVerificationStatus('failed');
          });
      } else {
        // Payment ID yoksa, session ID ile backend'de arama yapabilirsiniz
        // Şimdilik başarılı kabul edelim
        setVerificationStatus('success');
        // Ödeme başarılı olduğunda userTherapyPlanner sayfasına yönlendir
        setTimeout(() => {
          navigate('/userTherapyPlanner');
        }, 3000); // 3 saniye bekleyip yönlendir
      }
    } else {
      setVerificationStatus('failed');
    }
  }, [searchParams, dispatch, navigate]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoToPayments = () => {
    navigate('/my-payments'); // Ödemeler sayfanıza yönlendirin
  };

  const handleGoToTherapyPlanner = () => {
    navigate('/userTherapyPlanner');
  };

  if (verificationStatus === 'verifying' || verifyLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="bg-white rounded-[30px] p-8 max-w-md w-full mx-4 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="font-poppins font-bold text-xl text-primary mb-2">
            {t("verifyingPayment")}
          </h2>
          <p className="font-poppins text-gray-600">
            {t("paymentVerificationMessage")}
          </p>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#E8FFEE]">
        <div className="bg-white rounded-[30px] p-8 max-w-md w-full mx-4 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          
          <h2 className="font-poppins font-bold text-2xl text-primary mb-4">
            {t("paymentSuccessful")}
          </h2>
          
          <p className="font-poppins text-gray-600 mb-6">
            {t("paymentSuccessMessage")}
            You will be redirected to the therapy planner in 3 seconds...
          </p>
          
          {paymentDetails && (
            <div className="bg-[#E8FFEE] rounded-[20px] p-4 mb-6 text-left">
              <h3 className="font-poppins font-bold text-primary mb-2">{t("paymentDetails")}</h3>
              <div className="space-y-1 text-sm">
                <p>
                  <span className="font-medium">{t("paymentIdLabel")}</span> {paymentDetails.paymentId}
                </p>
                <p>
                  <span className="font-medium">{t("paymentAmountLabel")}</span> £{paymentDetails.amount}
                </p>
                <p>
                  <span className="font-medium">{t("paymentStatusLabel")}</span> {paymentDetails.status}
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <button
              onClick={handleGoToTherapyPlanner}
              className="w-full py-3 px-4 rounded-[25px] bg-primary text-white font-poppins font-bold hover:bg-green-700 transition-colors duration-300"
            >
              {t("goToTherapyPlanner")}
            </button>
            <button
              onClick={handleGoToPayments}
              className="w-full py-3 px-4 rounded-[25px] border border-primary text-primary font-poppins font-bold hover:bg-gray-50 transition-colors duration-300"
            >
              {t("viewMyPayments")}
            </button>
            <button
              onClick={handleGoHome}
              className="w-full py-3 px-4 rounded-[25px] border border-primary text-primary font-poppins font-bold hover:bg-gray-50 transition-colors duration-300"
            >
              {t("goHome")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Failed state
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#E8FFEE]">
      <div className="bg-white rounded-[30px] p-8 max-w-md w-full mx-4 text-center">
        {/* Error Icon */}
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </div>
        
        <h2 className="font-poppins font-bold text-2xl text-red-600 mb-4">
          {t("paymentFailed")}
        </h2>
        
        <p className="font-poppins text-gray-600 mb-6">
          {t("paymentFailedDescription")}
        </p>
        
        {verifyError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
            {verifyError.message || "An error occurred"}
          </div>
        )}

        <div className="flex flex-col gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-full py-3 px-4 rounded-[25px] bg-primary text-white font-poppins font-bold hover:bg-green-700 transition-colors duration-300"
          >
            {t("tryAgain")}
          </button>
          <button
            onClick={handleGoHome}
            className="w-full py-3 px-4 rounded-[25px] border border-primary text-primary font-poppins font-bold hover:bg-gray-50 transition-colors duration-300"
          >
            {t("goHome")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
